import { useState, useEffect, useRef } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { MultiSelect } from  '@/components/ui/multi-select';
import { useCategories } from '@/hooks/useCategories';
import { HierarchicalCategorySelect } from './HierarchicalCategorySelect';
import { usePlans } from '@/hooks/usePlans';
import { useTags } from '@/hooks/useTags';
import { useChannelTypes } from '@/hooks/useChannelTypes';
import {  Loader2 } from 'lucide-react';
import type { ChannelFormData } from '../types';
import { channelSchema, ChannelType, ChannelNumberInput } from '../types';
import { useProvider, useProviders } from '@/hooks/useProviders';
import { useNextSequenceNumber } from '@/hooks/useChannels';
import { ChannelTypeModal } from './add-channel-types/ChannelTypeModal';
import { cn } from '@/lib/utils';
import { useChannelStore } from "@/stores/channelStore";
import { FieldLegend as UiFieldLegend, FieldSet as UiFieldSet, Field as UiField, FieldContent as UiFieldContent, FieldTitle as UiFieldTitle } from "@/components/ui/field";
import { InputGroup, InputGroupInput } from "@/components/ui/input-group";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { HelpCircle } from 'lucide-react';

interface ChannelFormProps {
  providerId: number;
  versionId: number;
  defaultValues?: Partial<ChannelFormData>;
  onSubmit: (data: ChannelFormData) => void;
  isSubmitting?: boolean;
}

// Helper function to get initial numbers based on types and defaultValues
const getInitialNumbers = (
  types: ChannelType[] | undefined | null,
  defaultValues: Partial<ChannelFormData> | undefined,
  isSequence: boolean,
  nextNumber: number | undefined | null
): ChannelNumberInput[] => {
  if (!types || types.length === 0) {
    return defaultValues?.numbers || [];
  }

  // Start with numbers from defaultValues if available
  const existingNumbersMap = new Map(
    defaultValues?.numbers?.map(n => [n.typeId, n]) || []
  );

  // Map types to numbers, preserving existing values
  return types.map(type => {
    const existingNumber = existingNumbersMap.get(type.id);
    // For sequence type, always use nextNumber if available
    const numberValue = isSequence && type.is_sequence
      ? nextNumber?.toString() ?? ''
      : existingNumber?.number ?? ''; // Otherwise use existing or empty string

    return {
      typeId: type.id,
      number: numberValue,
      is_sequence: isSequence && type.is_sequence, // Only true if provider AND type are sequence
      is_default: type.is_default
    };
  });
};

export function ChannelForm({
  providerId,
  versionId,
  defaultValues,
  onSubmit,
  isSubmitting
}: ChannelFormProps) {
  const { currentRegionId } = useChannelStore();
  const { data: provider } = useProvider(providerId);
  const isSequence = provider?.has_sequence_numbers ?? false;
  const { data: nextNumber } = useNextSequenceNumber(providerId, versionId);
  const { data: categories } = useCategories(providerId, versionId);
  const { data: plans } = usePlans(providerId, versionId);
  const { data: tags } = useTags(providerId, versionId, currentRegionId || 0);
  const { data: types } = useChannelTypes(providerId, versionId);
  const { data: providers } = useProviders();
  const [isTypeModalOpen, setIsTypeModalOpen] = useState(false);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<number[]>(
    defaultValues?.category_ids || 
    (defaultValues?.category_id !== null && defaultValues?.category_id !== undefined 
      ? [defaultValues.category_id] 
      : [])
  );
  const [defaultCategoryId, setDefaultCategoryId] = useState<number | null>(null);
  const [childCategoryId, setChildCategoryId] = useState<number | null>(null);

  const form = useForm<ChannelFormData>({
    resolver: zodResolver(channelSchema),
    defaultValues: {
      name: '',
      description: '',
      category_id: null,
      category_ids: [],
      default_category_id: null,
      numbers: [],
      plan_ids: [],
      tag_ids: [],
      ...defaultValues
    },
    mode: 'onChange'
  });

  // Initialize useFieldArray for numbers
  // @ts-ignore
  const { fields, replace } = useFieldArray({
    control: form.control,
    name: "numbers",
    keyName: "fieldId" // Use a different key name than default 'id' to avoid conflicts
  });

  // Track if form has been initialized to prevent resetting during user edits
  const formInitializedRef = useRef(false);
  const lastDefaultValuesRef = useRef<typeof defaultValues | undefined>(undefined);

  // Reset initialization flag when modal closes (defaultValues becomes undefined)
  useEffect(() => {
    if (!defaultValues) {
      formInitializedRef.current = false;
      lastDefaultValuesRef.current = undefined;
    }
  }, [defaultValues]);

  // Effect to initialize/reset form state when defaultValues changes (not when types/nextNumber change)
  useEffect(() => {
    // Skip if types aren't loaded yet (they're needed for proper initialization)
    if (!types || types.length === 0) {
      console.log('[ChannelForm] Skipping effect: types not loaded', { typesLength: types?.length });
      return;
    }

    console.log('[ChannelForm] Effect triggered:', {
      hasDefaultValues: !!defaultValues,
      defaultValuesName: defaultValues?.name,
      numbersLength: defaultValues?.numbers?.length || 0,
      typesLength: types?.length || 0
    });

    // Check if defaultValues actually changed (new channel selected for editing)
    // Use a stable identifier comparison - compare by the numbers array or name
    const defaultValuesChanged =
      lastDefaultValuesRef.current === undefined ||
      (defaultValues && lastDefaultValuesRef.current?.name !== defaultValues?.name) ||
      JSON.stringify(lastDefaultValuesRef.current?.numbers) !== JSON.stringify(defaultValues?.numbers);

    console.log('[ChannelForm] defaultValuesChanged:', defaultValuesChanged);

    // Initialize form if:
    // 1. defaultValues changed (editing different channel)
    // 2. Form hasn't been initialized yet (new channel or first load)
    if (defaultValuesChanged || !formInitializedRef.current) {
      // Prepare the full default state including merged numbers
      const transformedTypes = types.map(type => ({
        ...type,
        display_name: type.name,
        is_default: type.is_default || false,
        is_sequence: type.is_sequence || false,
        provider_id: type.provider_id || 0,
        version_id: type.version_id || 0,
        created_at: type.created_at || undefined,
        updated_at: type.updated_at || undefined
      }));
      const initialNumbers = getInitialNumbers(transformedTypes, defaultValues, isSequence, nextNumber);
      const resetValues = {
        name: '',
        description: '',
        category_id: null,
        category_ids: [],
        default_category_id: null,
        plan_ids: [],
        tag_ids: [],
        ...defaultValues, // Apply passed defaultValues (if any)
        numbers: initialNumbers // Use the carefully prepared initial numbers
      };

      console.log('[ChannelForm] Resetting form with initialNumbers:', {
        initialNumbersLength: initialNumbers.length,
        initialNumbers
      });
      form.reset(resetValues); // Reset the entire form

      // CRITICAL: Sync useFieldArray with the new numbers
      console.log('[ChannelForm] Calling replace() to sync useFieldArray');
      replace(initialNumbers);

      // Initialize local state for categories after reset
      const resolvedCategoryIds = resetValues.category_ids || (resetValues.category_id ? [resetValues.category_id] : []);
      const resolvedDefaultCategoryId = resetValues.default_category_id || (resolvedCategoryIds.length > 0 ? resolvedCategoryIds[0] : null);

      setSelectedCategoryIds(resolvedCategoryIds);
      setDefaultCategoryId(resolvedDefaultCategoryId);

      // Mark form as initialized and update ref
      formInitializedRef.current = true;
      lastDefaultValuesRef.current = defaultValues;

      console.log('[ChannelForm] Form initialization complete', {
        fieldsLength: fields.length,
        mode: defaultValues ? 'edit' : 'add'
      });
    }
    // If defaultValues hasn't changed and form is already initialized, don't reset
    // This prevents losing user input when types/nextNumber refetch

  }, [defaultValues, types, isSequence, nextNumber, form.reset, replace]); // Keep dependencies but use refs to control behavior

  // Effect to sync local category state back to form state
  useEffect(() => {
    form.setValue('category_ids', selectedCategoryIds);
    form.setValue('default_category_id', defaultCategoryId);
    // Ensure category_id reflects the default category if one is set
    form.setValue('category_id', defaultCategoryId);

  }, [selectedCategoryIds, defaultCategoryId, form.setValue]); // Use setValue dependency

  // Keep child category in sync with selections
  useEffect(() => {
    if (!defaultCategoryId) {
      setChildCategoryId(null);
      return;
    }
    // If current child is not a child of the selected default category, clear it
    const parent = categories?.find(c => c.id === defaultCategoryId);
    const children = (parent as any)?.children as any[] | undefined;
    const valid = children?.some((c: any) => c.id === childCategoryId);
    if (!valid) {
      setChildCategoryId(null);
    }
  }, [defaultCategoryId, categories]);

  // When child category changes, ensure it's included in category_ids and becomes default
  useEffect(() => {
    if (childCategoryId) {
      setSelectedCategoryIds(prev => (prev.includes(childCategoryId) ? prev : [...prev, childCategoryId!]));
      setDefaultCategoryId(childCategoryId);
    }
  }, [childCategoryId]);

  const isFormValid = () => {
    // For each field in fieldErrors, check if it has errors
    return Object.values(form.formState.errors).length === 0;
  };
  
  // console.log('[ChannelForm] Current form state:', {
  //   defaultValues,
  //   errors: form.formState.errors,
  //   isDirty: form.formState.isDirty,
  //   isValid: isFormValid(),
  //   fieldErrors: Object.keys(form.formState.errors).length,
  //   selectedCategoryIds,
  //   defaultCategoryId,
  //   regionId: currentRegionId
  // });

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(data => {
        // console.log('[ChannelForm] Submit Data:', {
        //   ...data,
        //   price: data.price,
        //   description: data.description,
        //   is_local: data.is_local
        // });
        onSubmit(data);
      })} className="space-y-6">
        {/* Details: Name + Linked Provider */}
        <UiFieldSet>
          <UiFieldLegend>Details</UiFieldLegend>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Channel Name</FormLabel>
                  <FormControl>
                    <Input {...field} disabled={isSubmitting} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="linked_provider_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Linked Provider (Optional)</FormLabel>
                  <Select
                    value={field.value?.toString() || "none"}
                    onValueChange={(value) => field.onChange(value === "none" ? null : parseInt(value))}
                    disabled={isSubmitting}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a provider" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {providers?.map(provider => (
                        <SelectItem key={provider.id} value={provider.id.toString()}>
                          {provider.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </UiFieldSet>

        {/* Description + Price row */}
        <UiFieldSet>
          <UiFieldLegend>More Details</UiFieldLegend>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Input {...field} disabled={isSubmitting} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="price"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Price</FormLabel>
                  <FormControl>
                    <InputGroup>
                      <InputGroupInput
                        type="number"
                        step="0.01"
                        disabled={isSubmitting}
                        onChange={(e) => {
                          const value = e.target.value ? Number(e.target.value) : null;
                          field.onChange(value);
                        }}
                        value={field.value ?? ''}
                        placeholder="0.00"
                      />
                    </InputGroup>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </UiFieldSet>

        {/* Options: Local + Duplicate */}
        <UiFieldSet>
          <UiFieldLegend>Options</UiFieldLegend>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {currentRegionId && (
              <FormField
                control={form.control}
                name="is_local"
                render={({ field }) => (
                  <FormItem className="space-y-0">
                    <UiField orientation="horizontal" className="items-start">
                      <FormControl>
                        <Checkbox
                          checked={field.value}
                          onCheckedChange={field.onChange}
                          disabled={isSubmitting}
                        />
                      </FormControl>
                      <UiFieldContent>
                        <UiFieldTitle className="inline-flex items-center gap-2">
                          Local channel
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <HelpCircle className="h-4 w-4 text-muted-foreground cursor-help" />
                              </TooltipTrigger>
                              <TooltipContent>
                                Local channels can have the same name across different regions
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </UiFieldTitle>
                      </UiFieldContent>
                    </UiField>
                  </FormItem>
                )}
              />
            )}

            <FormField
              control={form.control}
              name="is_duplicate"
              render={({ field }) => (
                <FormItem className="space-y-0">
                  <UiField orientation="horizontal" className="items-start">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        disabled={isSubmitting}
                      />
                    </FormControl>
                    <UiFieldContent>
                      <UiFieldTitle className="inline-flex items-center gap-2">
                        Allow duplicates
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <HelpCircle className="h-4 w-4 text-muted-foreground cursor-help" />
                            </TooltipTrigger>
                            <TooltipContent>
                              Channels can share number and name with others
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </UiFieldTitle>
                    </UiFieldContent>
                  </UiField>
                </FormItem>
              )}
            />
          </div>
        </UiFieldSet>

        <UiFieldSet>
          <div className="flex items-center justify-between">
            <UiFieldLegend className="mb-0">Channel Numbers</UiFieldLegend>
            {!isSequence && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setIsTypeModalOpen(true)}
              >
                Manage Types
              </Button>
            )}
          </div>

          {isSequence ? (
            <FormField
              control={form.control}
              name="numbers.0.number"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Sequence Number</FormLabel>
                  <FormControl>
                    <InputGroup>
                      <InputGroupInput
                        {...field}
                        disabled
                        value={nextNumber || ''}
                        placeholder="Auto-generated"
                      />
                    </InputGroup>
                  </FormControl>
                  <FormDescription>
                    Next available sequence number will be assigned automatically
                  </FormDescription>
                </FormItem>
              )}
            />
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              {fields.map((field, index) => {
                const type = types?.find(t => t.id === field.typeId);
                if (!type) return null;

                return (
                  <FormField
                    key={field.fieldId}
                    control={form.control}
                    name={`numbers.${index}.number`}
                    render={({ field: inputField }) => (
                      <FormItem>
                        <FormLabel className="flex items-center gap-2">
                          {type.name}
                          {type.is_default && <span className="text-destructive">*</span>}
                        </FormLabel>
                        <FormControl>
                          <InputGroup className="min-w-0 max-w-[10rem]">
                            <InputGroupInput
                              {...inputField}
                              disabled={isSubmitting}
                              placeholder={type.is_default ? "Required" : "Optional"}
                              className={cn(
                                "w-24 text-center",
                                type.is_default && "border-primary",
                                type.is_default && form.formState.errors.numbers?.[index] && "border-destructive"
                              )}
                            />
                          </InputGroup>
                        </FormControl>
                        {type.is_default && form.formState.errors.numbers?.[index] && (
                          <FormMessage>Standard channel number is required</FormMessage>
                        )}
                      </FormItem>
                    )}
                  />
                );
              })}
            </div>
          )}
        </UiFieldSet>

        {/* Organization: Categories + Plans + Default + Child Category */}
        <UiFieldSet>
          <UiFieldLegend>Organization</UiFieldLegend>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FormField
              control={form.control}
              name="category_ids"
              render={() => (
                <FormItem>
                  <FormLabel>Categories</FormLabel>
                  <HierarchicalCategorySelect
                    categories={categories || []}
                    value={selectedCategoryIds}
                    onValueChange={(values) => {
                      setSelectedCategoryIds(values);
                      const newDefaultId = values.length > 0
                        ? (defaultCategoryId && values.includes(defaultCategoryId) ? defaultCategoryId : values[0])
                        : null;
                      setDefaultCategoryId(newDefaultId);
                    }}
                  />
                  <FormMessage />
                </FormItem>
              )}
            />

            {(() => {
              if (!defaultCategoryId) {
                return (
                  <div className="space-y-2">
                    <Label>Child Category</Label>
                    <p className="text-sm text-muted-foreground">Select a default category to see child categories</p>
                  </div>
                )
              }
              const parent = categories?.find(c => c.id === defaultCategoryId) as any;
              const children = (parent?.children as any[]) || [];
              if (!children.length) {
                return (
                  <div className="space-y-2">
                    <Label>Child Category</Label>
                    <p className="text-sm text-muted-foreground">No child category available</p>
                  </div>
                );
              }
              return (
                <div className="space-y-2">
                  <Label>Child Category</Label>
                  <Select
                    value={childCategoryId ? childCategoryId.toString() : ""}
                    onValueChange={(value) => setChildCategoryId(value ? parseInt(value) : null)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a child category" />
                    </SelectTrigger>
                    <SelectContent>
                      {children.map((child: any) => (
                        <SelectItem key={child.id} value={child.id.toString()}>
                          {child.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              );
            })()}
          </div>

          {selectedCategoryIds.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <FormField
                control={form.control}
                name="default_category_id"
                render={() => (
                  <FormItem>
                    <FormLabel>Default Category</FormLabel>
                    <Select
                      value={defaultCategoryId?.toString() || ""}
                      onValueChange={(value) => setDefaultCategoryId(value ? parseInt(value) : null)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select a default category" />
                      </SelectTrigger>
                      <SelectContent>
                        {selectedCategoryIds.map(id => {
                          const category = categories?.find(c => c.id === id);
                          return category ? (
                            <SelectItem key={id} value={id.toString()}>
                              {category.name}
                            </SelectItem>
                          ) : null;
                        })}
                      </SelectContent>
                    </Select>
                    <FormDescription>
                      The default category is used for display when not showing in all categories
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Spacer to keep grid balanced */}
              <div className="hidden md:block" />
            </div>
          )}
        </UiFieldSet>

        <FormField
          control={form.control}
          name="plan_ids"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Plans</FormLabel>
              <MultiSelect
                value={field.value || []}
                onValueChange={field.onChange}
                options={plans?.map(plan => ({
                  label: plan.name,
                  value: plan.id
                })) || []}
              />
              <FormMessage />
            </FormItem>
          )}
        />

        {currentRegionId && tags && tags.length > 0 && (
          <FormField
            control={form.control}
            name="tag_ids"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Tags</FormLabel>
                <MultiSelect
                  value={field.value || []}
                  onValueChange={field.onChange}
                  options={tags.map(tag => ({
                    label: `${tag.name} (${tag.channel_count})`,
                    value: tag.id
                  }))}
                />
                <FormDescription>
                  Assign tags to help organize and categorize this channel
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        <Button
          type="submit"
          disabled={isSubmitting || !isFormValid()}
          className="w-full"
        >
          {isSubmitting ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : null}
          Save Channel
        </Button>

        <ChannelTypeModal
          open={isTypeModalOpen}
          onOpenChange={setIsTypeModalOpen}
          providerId={providerId}
          versionId={versionId}
        />
      </form>
    </Form>
  );
}
